import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api, setAccessToken, clearAccessToken } from "@/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setVendor(data.vendor);
      return data;
    } catch (err) {
      // 401/expired session — clear local state. Log non-auth errors for debugging.
      if (err?.response?.status !== 401) {
        console.error("[auth] /auth/me failed:", err?.message || err);
      }
      setUser(null);
      setVendor(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refresh();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    // Backend sets httpOnly cookies AND returns access_token in the body so
    // iframe contexts that block 3rd-party cookies still keep a session via
    // localStorage + Authorization: Bearer header.
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.access_token) setAccessToken(data.access_token);
    await refresh();
    return data;
  }, [refresh]);

  const register = useCallback(async (email, password, business_name) => {
    const { data } = await api.post("/auth/register", { email, password, business_name });
    if (data?.access_token) setAccessToken(data.access_token);
    await refresh();
    return data;
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("[auth] logout failed:", err?.message || err);
    }
    clearAccessToken();
    setUser(null);
    setVendor(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, vendor, loading, login, register, logout, refresh, setVendor }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}
